import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Autorenew as SubscriptionIcon,
  Cancel as CancelIcon,
  Restore as ReactivateIcon,
  CheckCircle as ActiveIcon,
  Warning as PastDueIcon,
  Block as CanceledIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { subscriptionsApi, publicPaymentApi, type Subscription, type SubscriptionStatus } from '../api';
import { useDocumentTitle } from '../hooks/core';

export const SubscriptionsPage: React.FC = () => {
  useDocumentTitle('Subscriptions');

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        // Check if subscriptions are enabled
        const config = await publicPaymentApi.getPublicConfig();
        setSubscriptionsEnabled(config.subscriptionsEnabled);

        // Only fetch subscriptions if enabled
        if (config.subscriptionsEnabled) {
          await fetchSubscriptions();
        } else {
          setLoading(false);
        }
      } catch (err) {
        setError('Failed to load configuration');
        setLoading(false);
        console.error(err);
      }
    };
    init();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const result = await subscriptionsApi.listSubscriptions();
      setSubscriptions(result.subscriptions || []);
      setError(null);
    } catch (err) {
      setError('Failed to load subscriptions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedSubscription) return;

    try {
      setActionLoading(selectedSubscription.id);
      await subscriptionsApi.cancelSubscription(selectedSubscription.id);
      await fetchSubscriptions();
      setCancelDialogOpen(false);
      setSelectedSubscription(null);
    } catch (err) {
      setError('Failed to cancel subscription');
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (subscription: Subscription) => {
    try {
      setActionLoading(subscription.id);
      await subscriptionsApi.reactivateSubscription(subscription.id);
      await fetchSubscriptions();
    } catch (err) {
      setError('Failed to reactivate subscription');
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusChip = (status: SubscriptionStatus, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd && status === 'active') {
      return {
        label: 'Canceling',
        color: 'warning' as const,
        icon: <PendingIcon />,
      };
    }

    switch (status) {
      case 'active':
        return { label: 'Active', color: 'success' as const, icon: <ActiveIcon /> };
      case 'past_due':
        return { label: 'Past Due', color: 'error' as const, icon: <PastDueIcon /> };
      case 'canceled':
        return { label: 'Canceled', color: 'default' as const, icon: <CanceledIcon /> };
      case 'unpaid':
        return { label: 'Unpaid', color: 'error' as const, icon: <PastDueIcon /> };
      default:
        return { label: status, color: 'default' as const, icon: <PendingIcon /> };
    }
  };

  const formatInterval = (interval?: string, intervalCount?: number): string => {
    if (!interval) return 'One-time';
    const count = intervalCount || 1;
    if (count === 1) {
      return interval === 'month' ? 'Monthly' : 'Yearly';
    }
    return `Every ${count} ${interval}s`;
  };

  const hasActiveSubscription = subscriptions.some(
    s => s.status === 'active' && !s.cancelAtPeriodEnd
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show disabled message if subscriptions are not enabled
  if (subscriptionsEnabled === false) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Subscriptions
        </Typography>

        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SubscriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Subscriptions Not Available
          </Typography>
          <Typography color="text.secondary">
            Subscription billing is not currently enabled. Please check back later.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Subscriptions
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your recurring subscriptions and billing.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SubscriptionIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5">Your Subscriptions</Typography>
        </Box>

        {subscriptions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SubscriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Active Subscriptions
            </Typography>
            <Typography color="text.secondary">
              You don't have any subscriptions yet. Subscribe to a plan to get started.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Billing</TableCell>
                  <TableCell>Current Period</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((subscription) => {
                  const statusChip = getStatusChip(subscription.status, subscription.cancelAtPeriodEnd);
                  const isLoading = actionLoading === subscription.id;

                  return (
                    <TableRow key={subscription.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {subscription.product?.name || 'Subscription'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subscription.product?.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusChip.label}
                          color={statusChip.color}
                          size="small"
                          icon={statusChip.icon}
                        />
                        {subscription.mode === 'test' && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Test Mode
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${((subscription.price?.amount || 0) / 100).toFixed(2)}
                          {subscription.price?.interval && (
                            <span> / {subscription.price.interval}</span>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatInterval(subscription.price?.interval, subscription.price?.intervalCount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                        </Typography>
                        {subscription.cancelAtPeriodEnd && (
                          <Typography variant="caption" color="warning.main">
                            Cancels {formatDate(subscription.currentPeriodEnd)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {subscription.status === 'active' && subscription.cancelAtPeriodEnd ? (
                          <Button
                            size="small"
                            startIcon={isLoading ? <CircularProgress size={16} /> : <ReactivateIcon />}
                            onClick={() => handleReactivate(subscription)}
                            disabled={isLoading}
                          >
                            Reactivate
                          </Button>
                        ) : subscription.status === 'active' ? (
                          <Button
                            size="small"
                            color="error"
                            startIcon={isLoading ? <CircularProgress size={16} /> : <CancelIcon />}
                            onClick={() => handleCancelClick(subscription)}
                            disabled={isLoading}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {hasActiveSubscription && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Your subscription will automatically renew at the end of each billing period.
              You can cancel anytime and continue using the service until the end of your current period.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your subscription will remain active until the end of your current billing period
            on {selectedSubscription && formatDate(selectedSubscription.currentPeriodEnd)}.
            You can reactivate your subscription at any time before then.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            disabled={actionLoading === selectedSubscription?.id}
            startIcon={actionLoading === selectedSubscription?.id ? <CircularProgress size={16} /> : null}
          >
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
